import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanCMSApi, post, remove } from "~/Services";
import { eramanHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import {
  renderActionsColumn,
  renderDateColumn,
  renderImageColumn,
  renderPublishedColumn,
} from "~/Utils/TableRenderFunction";
import { useBrandDocumentDetails } from "./hooks/Brand.hook";

export const Brands = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const [sortOrder, setSortOrder] = useState<string>("asc"); // default asc
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
  ];

  const { data: drilldown, refetch: refetchDocument } = useBrandDocumentDetails(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    if (query.get("subActiveTab") === "outlet") {
      setSelectedPublished("");
      setSearchValue("");
      setCurrentPage(0);
    }
  }, [query.get("subActiveTab")]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = decodeHTML(searchValue).toLowerCase();
      filteredData = filteredData.filter((item: any) => decodeHTML(item.title)?.toLowerCase().includes(searchLower));
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const decodeHtmlEntities = (str: string) => {
    if (!str) return str;
    return str
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/O\u0026#039;/g, "");
  };

  // const getRecords = () => {
  //   setIsLoading(true);
  //   let url = `${eramanHost}/api/get-all-foodandbeverage-brands`;
  //   axios
  //     .get(url)
  //     .then((res: any) => {
  //       if (res && res.data) {
  //         let envelopes = res.data;
  //         setTotalRecords(envelopes.length);
  //         setData(envelopes);
  //       }
  //       setIsLoading(false);
  //     })
  //     .catch(() => {
  //       setData([]);
  //       setIsLoading(false);
  //     });
  // };

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/get-all-foodandbeverage-brands`;
    eramanCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data.map((item: any) => ({
            ...item,
            // ✅ decode HTML entities in title
            title: decodeHtmlEntities(item.title),
          }));

          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setData([]);
        setIsLoading(false);
      });
  };
  const handleDelete = async (id: string) => {
    remove(`${eramanHost}/api/all-brands/${id}`)
      .then(() => {
        getRecords();
      })
      .catch(() => {});
  };

  const columns: TableColumnsType = [
    {
      title: "Image",
      dataIndex: "field_common_image",
      key: "field_common_image",
      render: (images: string) => renderImageColumn(images),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "field_created_at_brands",
      key: "field_created_at_brands",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_created_at_brands"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_created_at_brands"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "field_updated_at_brands",
      key: "field_updated_at_brands",
      render: (value: any, record: any) => {
        if (value && record.field_created_at_brands && value === record.field_created_at_brands) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "field_updated_at_brands"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "field_updated_at_brands"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/product-management/food-and-beverages-brand/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        );
      },
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  // ✅ Only asc <-> desc toggle
  const handleTableSort = (column: any) => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);
    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, newOrder, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Product Management",
      subModule: "fAndBBrands",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        }
      })
      .catch(() => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteBrandRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${eramanHost}/api/food-and-beverages-brands-list-page-crud-api/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch {}
  }

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  title: decodeHTML(d.title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "fAndBBrands") as ModuleConfig]}
        data={drilldown}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteBrandRequest}
      />
    </>
  );
};

export function handleSort(a: any, b: any, order: string | undefined, sortBy: string) {
  // Special handling for field_faqs_question (if applicable)
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (sortBy === "title") {
    const valueA = a[sortBy] ? String(a[sortBy]).toLowerCase() : "";
    const valueB = b[sortBy] ? String(b[sortBy]).toLowerCase() : "";

    if (order === "desc") {
      return valueA < valueB ? 1 : -1;
    } else if (order === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

export function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
